import React, { useMemo, useState } from 'react';
import { Link } from '@remix-run/react';
import { cn } from '@rocco/utils/cn';
import { useTracker } from '@archipro-website/tracker';
import { trackBreadcrumbClick } from '~/modules/tracking/util/trackBreadcrumbClick';

interface TitleSectionProps {
    /** The category name. */
    category: string;
    /** The root category name. */
    rootCategory?: string;
    /** The description text. */
    categoryDescription: string;
    /** The number of items in the category. */
    itemsInCategory: number;
    /** The breadcrumbs data. */
    breadcrumbs?: { href: string; label: string }[];
    /** Show the category title. Defaults to `true`. */
    showCategoryTitle?: boolean;
    /** The category is the root category. Defaults to `false`. */
    isRoot?: boolean;
}

const CATEGORY_DESCRIPTION =
    "Find everything you need to build, whether it's a tap or a cladding system. Connect to purchase directly from New Zealand's leading product suppliers.";

/**
 * The title header section of the directory pages (professionals, products, projects).
 */
export const TitleSectionDesktop = ({
    category,
    rootCategory,
    categoryDescription,
    breadcrumbs,
    showCategoryTitle = true,
    isRoot = false,
}: TitleSectionProps) => {
    const [isExpandDescription, setIsExpandDescription] = useState(false);
    const tracker = useTracker();
    const allBreadcrumbs = useMemo(
        () =>
            breadcrumbs?.length
                ? [{ href: '/', label: 'Home' }, ...breadcrumbs]
                : null,
        [breadcrumbs]
    );

    return (
        <div className="mb-28 px-12.5 pt-30">
            {allBreadcrumbs && (
                <div className="flex items-center gap-3.5">
                    {allBreadcrumbs.map((breadcrumb, index) => (
                        <React.Fragment key={breadcrumb.href}>
                            <Link
                                to={breadcrumb.href}
                                onClick={() => {
                                    trackBreadcrumbClick(
                                        tracker,
                                        breadcrumb.href
                                    );
                                }}
                            >
                                <span className="text-2.5 font-medium uppercase tracking-[0.05rem] text-[#848484] hover:text-gray-700">
                                    {breadcrumb.label}
                                </span>
                            </Link>
                            {index !== allBreadcrumbs.length - 1 && (
                                <span className="text-2.5 font-medium uppercase tracking-[0.05rem] text-[#848484]">{`/`}</span>
                            )}
                        </React.Fragment>
                    ))}
                </div>
            )}
            <div className="flex items-start justify-between">
                {showCategoryTitle && (
                    <div className="relative flex-[3.25]">
                        {isRoot ? (
                            <h1 className="-ml-5 inline font-sans text-62.5 leading-none tracking-[-0.04em] text-black">
                                {rootCategory ?? category}
                            </h1>
                        ) : (
                            <>
                                <div
                                    // Use before pseudo element for SEO reasons.
                                    className="inline before:pointer-events-none before:-ml-5 before:text-62.5 before:leading-none before:tracking-[-0.04em] before:text-gray-300 before:content-[attr(data-giant-text-content)]"
                                    aria-hidden="true"
                                    data-giant-text-content={
                                        rootCategory ?? category
                                    }
                                />
                                <div className="absolute m-2 inline w-70 text-lg text-black">
                                    {category}
                                </div>
                            </>
                        )}
                    </div>
                )}
                <div
                    className={cn(
                        'max-w-212 flex-1 pr-18',
                        showCategoryTitle && 'ml-32'
                    )}
                >
                    <div
                        className={cn(
                            'font-sans text-base leading-[1.3] text-gray-800',
                            !isExpandDescription &&
                                'line-clamp-6 cursor-pointer'
                        )}
                        dangerouslySetInnerHTML={{
                            __html: categoryDescription || CATEGORY_DESCRIPTION,
                        }}
                        onClick={() => setIsExpandDescription(true)}
                    ></div>
                </div>
            </div>
        </div>
    );
};
